import { useCallback, useEffect, useState } from 'react';
import DashboardPageFrame from '../components/DashboardPageFrame.jsx';
import ProblemRecordEditor from '../components/ProblemRecordEditor.jsx';
import RiskRecordEditor from '../components/RiskRecordEditor.jsx';
import ScheduleRecordEditor from '../components/ScheduleRecordEditor.jsx';
import {
  getProblems, getProgressSchedules, getRisks,
} from '../api/dashboardApi.js';
import { DASHBOARD_TABS } from '../constants/tabs.js';
import {
  toDate,
  formatDateLabel,
  toPercent,
  normalizeImpactLevel,
  normalizeRegionLabel,
  countByImpactLevel,
  groupDetailsByImpact,
  isRowCompleted,
  makeTaskDetail,
  calcTaskData,
  buildRegionCards,
  maxImpactCount,
} from '../utils/regionRiskConverters.js';
import '../styles/dashboard-region-risk.css';

const TASK_LEVELS = [
  { key: 'total', label: '総数', color: '#3b82f6' },
  { key: 'planned', label: '予定', color: '#22c55e' },
  { key: 'completed', label: '実績', color: '#f59e0b' },
  { key: 'delayed', label: '遅れ', color: '#ef4444' },
];

const IMPACT_LEVELS = [
  { key: 'H', label: 'H', color: '#ef4444', markerTone: 'critical' },
  { key: 'M', label: 'M', color: '#f59e0b', markerTone: 'warning' },
  { key: 'L', label: 'L', color: '#fbbf24', markerTone: 'warning' },
];


function ProgressRing({ rate }) {
  const normalized = Math.max(0, Math.min(100, Math.round(rate || 0)));
  const style = {
    background: `conic-gradient(#3b82f6 ${normalized * 3.6}deg, #d1d5db 0deg)`,
  };

  return (
    <div className="rrisk-progress-ring" style={style}>
      <div className="rrisk-progress-ring__inner">{normalized}%</div>
    </div>
  );
}

function TaskProgressBars({ taskCounts, onBarClick }) {
  const maxValue = Math.max(1, ...TASK_LEVELS.map((level) => taskCounts[level.key] || 0));

  return (
    <div className="rrisk-delay-chart">
      <p className="rrisk-delay-chart__title">タスク進捗</p>
      <div className="rrisk-delay-chart__bars">
        {TASK_LEVELS.map((level) => {
          const value = taskCounts[level.key] || 0;
          const height = value > 0 ? Math.max(8, (value / maxValue) * 66) : 0;
          const clickable = value > 0 && onBarClick;
          return (
            <div
              className={`rrisk-delay-bar ${clickable ? 'rrisk-delay-bar--clickable' : ''}`}
              key={level.key}
              onClick={clickable ? () => onBarClick(level.key) : undefined}
            >
              <span className="rrisk-delay-bar__value" style={{ color: level.color }}>{value}</span>
              <div className="rrisk-delay-bar__column-wrap">
                <div
                  className="rrisk-delay-bar__column"
                  style={{ height: `${height}px`, backgroundColor: level.color }}
                />
              </div>
              <span className="rrisk-delay-bar__label">{level.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailModal({ visible, onClose, title, items, onItemClick }) {
  if (!visible) return null;

  return (
    <div className="rrisk-modal-overlay" onClick={onClose}>
      <div className="rrisk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rrisk-modal__header">
          <h3 className="rrisk-modal__title">{title}</h3>
          <button className="rrisk-modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </div>
        <div className="rrisk-modal__body">
          {/* istanbul ignore next -- モーダルは件数のある行からしか開かないため空配列にならない */ items.length === 0 ? (
            <p className="rrisk-modal__empty">データがありません</p>
          ) : (
            <ul className="rrisk-modal__list">
              {items.map((item, idx) => {
                const hasDetailPopup = item.rawRow && onItemClick;
                return (
                  <li className="rrisk-modal__item" key={idx}>
                    <div className="rrisk-modal__item-main">
                      {hasDetailPopup ? (
                        <button
                          className="rrisk-modal__link-btn"
                          onClick={() => onItemClick(item)}
                          type="button"
                        >
                          {item.title}
                        </button>
                      ) : (
                        <span className="rrisk-modal__text">{item.title}</span>
                      )}
                      {item.owner && (
                        <span className="rrisk-modal__owner">{item.owner}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function BreakdownSection({ title, total, counts, details, markerTone, onRowClick }) {
  const maxValue = maxImpactCount(counts);

  return (
    <section className="rrisk-breakdown">
      <div className="rrisk-breakdown__header">
        <span className={`rrisk-breakdown__marker rrisk-breakdown__marker--${markerTone}`}>&#9888;</span>
        <h4 className="rrisk-breakdown__title">{title}: {total}</h4>
      </div>
      <div className="rrisk-breakdown__rows">
        {IMPACT_LEVELS.map((level) => {
          const value = counts[level.key] || 0;
          const width = value > 0 ? Math.max(6, (value / maxValue) * 100) : 0;
          const clickable = value > 0 && details;
          return (
            <div
              className={`rrisk-breakdown__row ${clickable ? 'rrisk-breakdown__row--clickable' : ''}`}
              key={level.key}
              onClick={clickable ? () => onRowClick(level.key) : undefined}
            >
              <span className="rrisk-breakdown__level">{level.label}</span>
              <div className="rrisk-breakdown__bar-track">
                <div
                  className="rrisk-breakdown__bar-fill"
                  style={{ width: `${width}%`, backgroundColor: level.color }}
                />
              </div>
              <span className="rrisk-breakdown__count">{value}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RegionRiskCard({ data, onOpenModal }) {
  const issueTotal = (data.issueCounts.H || 0) + (data.issueCounts.M || 0) + (data.issueCounts.L || 0);
  const riskTotal = (data.riskCounts.H || 0) + (data.riskCounts.M || 0) + (data.riskCounts.L || 0);

  return (
    <article className="rrisk-card">
      <h3 className="rrisk-card__title">{data.regionName}</h3>

      <div className="rrisk-card__summary">
        <ProgressRing rate={data.progressRate} />
        <TaskProgressBars
          taskCounts={data.taskCounts}
          onBarClick={(key) => {
            const labelMap = { total: '総数タスク', planned: '予定タスク', completed: '実績タスク', delayed: '遅れタスク' };
            onOpenModal(`${data.regionName} - ${labelMap[key]}`, /* istanbul ignore next -- 件数0のバーは非クリックなので必ず存在する */ (data.taskDetails?.[key] || []));
          }}
        />
      </div>

      <p className="rrisk-card__achievement">{data.achievementText}</p>
      <div className="rrisk-card__dates">
        <p>{data.startDateText}</p>
        <p>{data.endDateText}</p>
      </div>

      <div className="rrisk-card__breakdowns">
        <BreakdownSection
          counts={data.issueCounts}
          details={data.issueDetails}
          markerTone="critical"
          title="課題"
          total={issueTotal}
          onRowClick={(level) =>
            onOpenModal(`${data.regionName} - 課題 (${level})`, /* istanbul ignore next */ (data.issueDetails?.[level] || []))
          }
        />
        <BreakdownSection
          counts={data.riskCounts}
          details={data.riskDetails}
          markerTone="warning"
          title="リスク"
          total={riskTotal}
          onRowClick={(level) =>
            onOpenModal(`${data.regionName} - リスク (${level})`, /* istanbul ignore next -- 件数0の階層は非クリックなので必ず存在する */ (data.riskDetails?.[level] || []))
          }
        />
      </div>
    </article>
  );
}

export default function DashboardRegionRiskPage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalItems, setModalItems] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editingProblem, setEditingProblem] = useState(null);
  const [editingRisk, setEditingRisk] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [scheduleData, problemData, riskData] = await Promise.all([
        getProgressSchedules(),
        getProblems(),
        getRisks(),
      ]);
      const builtCards = buildRegionCards(scheduleData, problemData, riskData);
      setCards(builtCards);
    } catch {
      setError('課題・リスク（地域別）データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    loadData().finally(() => { if (!active) return; });
    return () => { active = false; };
  }, [loadData]);

  const displayedCards = cards;

  const openModal = useCallback((title, items) => {
    setModalTitle(title);
    setModalItems(items);
    setModalVisible(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalVisible(false);
    setModalItems([]);
  }, []);

  // モーダルのitemsは開いた時点のスナップショットのため、保存・削除後は閉じて再取得し、
  // 古いupdated_at_isoでの再編集（偽409）や削除済みレコードの再表示を防ぐ
  const handleEditorSaved = useCallback(() => {
    closeModal();
    loadData();
  }, [closeModal, loadData]);

  const openItemDetail = useCallback((item) => {
    /* istanbul ignore next -- 一覧の要素は常に rawRow を持つ */
    const row = item.rawRow || item;
    const type = item.type;
    if (type === 'problem') {
      setEditingProblem({ ...row });
    } else if (type === 'risk') {
      setEditingRisk({ ...row });
    } else {
      setEditingRow({ ...row });
    }
  }, []);

  const closeScheduleDetail = useCallback(() => {
    setEditingRow(null);
  }, []);

  const closeProblemDetail = useCallback(() => {
    setEditingProblem(null);
  }, []);

  const closeRiskDetail = useCallback(() => {
    setEditingRisk(null);
  }, []);

  return (
    <DashboardPageFrame
      mainClassName="rrisk-main"
      rootClassName="rrisk-page"
      secondaryTabActiveIndex={3}
      secondaryTabs={DASHBOARD_TABS}
    >
      <section className="rrisk-board" data-node-id="1167:1656">
        {displayedCards.map((card) => (
          <RegionRiskCard data={card} key={/* istanbul ignore next */ card.regionKey || card.regionName} onOpenModal={openModal} />
        ))}
      </section>

      {loading && <p className="rrisk-message">読み込み中...</p>}
      {!loading && error && <p className="rrisk-message rrisk-message--error">{error}</p>}

      <DetailModal
        items={modalItems}
        title={modalTitle}
        visible={modalVisible}
        onClose={closeModal}
        onItemClick={openItemDetail}
      />

      <ScheduleRecordEditor
        initialRow={editingRow}
        onClose={closeScheduleDetail}
        onDeleted={handleEditorSaved}
        onSaved={handleEditorSaved}
        open={Boolean(editingRow)}
      />

      <ProblemRecordEditor
        initialRow={editingProblem}
        onClose={closeProblemDetail}
        onDeleted={handleEditorSaved}
        onSaved={handleEditorSaved}
        open={Boolean(editingProblem)}
      />

      <RiskRecordEditor
        initialRow={editingRisk}
        onClose={closeRiskDetail}
        onDeleted={handleEditorSaved}
        onSaved={handleEditorSaved}
        open={Boolean(editingRisk)}
      />
    </DashboardPageFrame>
  );
}
